import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Download, Loader2, Copy, ShieldOff } from "lucide-react";
import api, { API_BASE_URL, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RESET_PHRASE = "RESET PRODUCTION DATA";
const COUNTDOWN_SECONDS = 5;

/**
 * Phase DZ — Danger Zone card.
 *
 * Renders ONLY for super_admin. Defense-in-depth on top of the
 * backend's ``confirmation_phrase`` + ``backup_acknowledged`` gates:
 *
 *   Outer card gates (must satisfy ALL before "Reset" button enables):
 *     1. Backup has been downloaded this session.
 *     2. Outer checkbox: "I have saved this backup somewhere safe".
 *
 *   Inner modal gates (must satisfy ALL before "Confirm" button enables):
 *     3. Inner checkbox: "I understand this is irreversible".
 *     4. Typed-confirmation phrase matches RESET_PHRASE exactly.
 *     5. 5-second countdown completes (resets if either of 3/4 becomes
 *        invalid mid-countdown).
 *
 * The five layers are explicitly designed to defeat racing/automated
 * agents: they require a real human's serial attention across a typed
 * input, two checkboxes, and a forced wait. Each layer is independent —
 * defeating one doesn't defeat the rest.
 */
export function DangerZoneCard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [backupDownloading, setBackupDownloading] = useState(false);
    const [backupDownloaded, setBackupDownloaded] = useState(false);
    const [backupSavedAck, setBackupSavedAck] = useState(false);

    const [resetOpen, setResetOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState("");

    const [irreversibleAck, setIrreversibleAck] = useState(false);
    const [phrase, setPhrase] = useState("");
    const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const [resetResult, setResetResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const copyTimer = useRef(null);

    // ----- Backup download ------------------------------------------------
    //
    // We deliberately use a raw ``fetch`` (not the shared axios instance)
    // for the binary download. Two reasons:
    //
    //   1. axios with ``responseType: "blob"`` stores error bodies as a
    //      ``Blob`` on ``err.response.data`` — ``formatApiError`` can't
    //      read the JSON ``detail`` from it, so genuine 401/403/500
    //      responses surface in the UI as the misleading literal string
    //      "Network Error" (axios's fallback for ``err.message``).
    //   2. The global axios 401-interceptor races with blob streaming
    //      and triggers ``window.location.assign("/login")`` mid-download,
    //      cancelling the in-flight response and again surfacing as a
    //      "network" failure rather than an auth failure.
    //
    // The dedicated ``fetch`` path here decodes the error body explicitly
    // and lets the catch block surface a real message.
    const onDownloadBackup = useCallback(async () => {
        setBackupDownloading(true);
        try {
            const token = localStorage.getItem("rp_token");
            if (!token) {
                throw new Error("You are signed out — please log in again.");
            }
            // IMPORTANT — use the same base URL the rest of the app's axios
            // instance uses. Building the URL ad-hoc from
            // ``process.env.REACT_APP_BACKEND_URL`` here is a foot-gun: a
            // trailing slash, an empty env, or a prod build that uses
            // same-origin routing all produce subtly different URLs and
            // surface as "Failed to fetch" on prod even when axios works.
            const url = `${API_BASE_URL}/super-admin/backup`;
            const res = await fetch(url, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
                // Match axios's default (withCredentials=false). The auth
                // contract here is the Bearer JWT only — no cookies — and
                // ``credentials: "include"`` would force the backend's
                // CORS to echo a specific origin (it currently uses ``*``
                // on preview), so we keep this off.
                credentials: "same-origin",
            });
            if (!res.ok) {
                // Decode the error body explicitly — the server returns
                // ``{"detail": "..."}`` for both string and structured
                // errors.
                let msg = `Backup failed (HTTP ${res.status}).`;
                try {
                    const text = await res.text();
                    const parsed = JSON.parse(text);
                    const det = parsed?.detail;
                    if (typeof det === "string") msg = det;
                    else if (det?.message) msg = det.message;
                } catch {
                    /* keep generic msg */
                }
                if (res.status === 401) {
                    msg = "Your session has expired. Please log in again.";
                }
                throw new Error(msg);
            }
            const blob = await res.blob();
            const cd = res.headers.get("content-disposition") || "";
            const match = /filename="([^"]+)"/.exec(cd);
            const filename = match
                ? match[1]
                : `rentflow-backup-${new Date().toISOString().replace(/[:.]/g, "-")}.zip`;
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
            setBackupDownloaded(true);
            toast.success("Backup downloaded — keep this file safe.");
        } catch (err) {
            // ``fetch`` itself throws ``TypeError: Failed to fetch`` when
            // the request never reached the server (DNS, CORS preflight
            // reject, TLS, blocked mixed content). Surface a clearer hint
            // so the operator knows it's a transport-level problem, not
            // a server-side bug.
            let msg = err?.message || "Backup download failed.";
            if (msg === "Failed to fetch" || msg.includes("NetworkError")) {
                msg = (
                    "Could not reach the backup endpoint. " +
                    "Check your network, then ask Emergent support if it persists."
                );
            }
            toast.error(msg);
        } finally {
            setBackupDownloading(false);
        }
    }, []);

    // ----- Open reset modal + fetch preview --------------------------------

    const openResetModal = useCallback(async () => {
        setResetOpen(true);
        setPhrase("");
        setIrreversibleAck(false);
        setCountdown(COUNTDOWN_SECONDS);
        setSubmitError("");
        setResetResult(null);
        setPreview(null);
        setPreviewLoading(true);
        setPreviewError("");
        try {
            const { data } = await api.get("/super-admin/factory-reset/preview");
            setPreview(data);
        } catch (err) {
            setPreviewError(formatApiError(err));
        } finally {
            setPreviewLoading(false);
        }
    }, []);

    const closeResetModal = useCallback(() => {
        // Block close while submitting so the operator can't accidentally
        // walk away from a half-completed wipe.
        if (submitting) return;
        setResetOpen(false);
    }, [submitting]);

    // ----- Inner-gate countdown -------------------------------------------
    // Whenever the two inner gates (irreversibleAck + phrase match) are
    // both satisfied, run a 5→0 countdown. If either becomes invalid mid-
    // countdown, snap back to the full COUNTDOWN_SECONDS.
    const phraseMatches = phrase === RESET_PHRASE;
    const innerGatesSatisfied = irreversibleAck && phraseMatches;
    useEffect(() => {
        if (!resetOpen || resetResult) return undefined;
        if (!innerGatesSatisfied) {
            setCountdown(COUNTDOWN_SECONDS);
            return undefined;
        }
        if (countdown <= 0) return undefined;
        const id = setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
        return () => clearTimeout(id);
    }, [resetOpen, resetResult, innerGatesSatisfied, countdown]);

    // ----- Submit reset ----------------------------------------------------

    const onConfirmReset = useCallback(async () => {
        setSubmitting(true);
        setSubmitError("");
        try {
            const { data } = await api.post("/super-admin/factory-reset", {
                confirmation_phrase: phrase,
                backup_acknowledged: true,
            });
            setResetResult(data);
        } catch (err) {
            setSubmitError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }, [phrase]);

    // ----- Copy + logout ---------------------------------------------------

    const onCopyPassword = useCallback(() => {
        if (!resetResult?.new_password) return;
        try {
            navigator.clipboard.writeText(resetResult.new_password);
            setCopied(true);
            if (copyTimer.current) clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Copy failed — select the password manually.");
        }
    }, [resetResult]);

    const onLogoutAfterReset = useCallback(() => {
        logout();
        navigate("/login");
    }, [logout, navigate]);

    if (user?.role !== "super_admin") return null;

    const canSubmit =
        innerGatesSatisfied
        && countdown === 0
        && !submitting
        && !resetResult
        && !!preview;

    return (
        <Card
            className="border-2 border-rose-300 bg-rose-50/40"
            data-testid="danger-zone-card"
        >
            <CardHeader>
                <div className="flex items-center gap-2">
                    <ShieldOff size={18} className="text-rose-700" />
                    <CardTitle className="font-display text-xl text-rose-900">
                        Danger Zone
                    </CardTitle>
                </div>
                <CardDescription className="text-rose-800/80">
                    Destructive actions for the whole tenant. Read the warnings carefully.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="rounded-md bg-white border border-rose-200 p-4 space-y-3">
                    <div className="flex items-start gap-3">
                        <Download size={16} className="text-blue-900 mt-1 shrink-0" />
                        <div>
                            <h3 className="font-semibold text-slate-900">Download backup</h3>
                            <p className="text-sm text-slate-600 mt-1">
                                Exports every database collection as JSON inside a single ZIP.
                                Uploaded files on disk are <strong>not</strong> included in this backup.
                            </p>
                        </div>
                    </div>
                    <Button
                        type="button"
                        onClick={onDownloadBackup}
                        disabled={backupDownloading}
                        data-testid="danger-download-backup-btn"
                    >
                        {backupDownloading ? (
                            <><Loader2 size={14} className="mr-2 animate-spin" /> Downloading…</>
                        ) : backupDownloaded ? (
                            <>Backup downloaded · download again</>
                        ) : (
                            <>Download backup ZIP</>
                        )}
                    </Button>
                </div>

                <div className="rounded-md bg-white border border-rose-200 p-4 space-y-3">
                    <div className="flex items-start gap-3">
                        <AlertTriangle size={16} className="text-rose-700 mt-1 shrink-0" />
                        <div>
                            <h3 className="font-semibold text-rose-900">Reset production data</h3>
                            <p className="text-sm text-rose-900/80 mt-1">
                                Wipes every customer, rental, contract, invoice, payment, notification,
                                template, and audit row. Keeps tenant branding, integration keys, and{" "}
                                <strong>only</strong> your super-admin account (with a fresh random password).
                            </p>
                        </div>
                    </div>

                    {/* Gate 1: backup downloaded. */}
                    {!backupDownloaded && (
                        <p className="text-xs text-rose-800 italic" data-testid="danger-gate-need-backup">
                            Download a backup first to enable this button.
                        </p>
                    )}

                    {/* Gate 2: explicit "saved somewhere safe" checkbox.
                        Decoupled from "the download button has been clicked" so a
                        bot racing through clicks can't reach the reset action. */}
                    {backupDownloaded && (
                        <label
                            className="flex items-start gap-2 text-sm text-rose-900 cursor-pointer select-none"
                            data-testid="danger-backup-saved-ack-label"
                        >
                            <Checkbox
                                checked={backupSavedAck}
                                onCheckedChange={(v) => setBackupSavedAck(v === true)}
                                className="mt-0.5"
                                data-testid="danger-backup-saved-ack-checkbox"
                            />
                            <span>
                                I have <strong>saved this backup</strong> somewhere safe (outside this pod).
                            </span>
                        </label>
                    )}

                    <Button
                        type="button"
                        variant="destructive"
                        onClick={openResetModal}
                        disabled={!backupDownloaded || !backupSavedAck}
                        data-testid="danger-open-reset-modal-btn"
                    >
                        Reset production data
                    </Button>
                </div>
            </CardContent>

            <Dialog
                open={resetOpen}
                onOpenChange={(o) => { if (!o) closeResetModal(); }}
            >
                <DialogContent
                    className="max-w-2xl"
                    data-testid="danger-reset-modal"
                    onInteractOutside={(e) => { if (submitting || resetResult) e.preventDefault(); }}
                    onEscapeKeyDown={(e) => { if (submitting || resetResult) e.preventDefault(); }}
                >
                    {resetResult ? (
                        <ResetCompleteView
                            result={resetResult}
                            copied={copied}
                            onCopy={onCopyPassword}
                            onLogout={onLogoutAfterReset}
                        />
                    ) : (
                        <>
                            <DialogHeader>
                                <DialogTitle className="text-rose-900 flex items-center gap-2">
                                    <AlertTriangle size={18} /> Confirm reset
                                </DialogTitle>
                                <DialogDescription>
                                    This action is <strong>permanent</strong>. Review the plan below, then type the
                                    confirmation phrase to enable the destructive button.
                                </DialogDescription>
                            </DialogHeader>

                            <div className="mt-2 max-h-[55vh] overflow-y-auto pr-2 space-y-4">
                                {previewLoading && (
                                    <div className="text-sm text-slate-500 flex items-center gap-2"
                                        data-testid="danger-preview-loading">
                                        <Loader2 size={14} className="animate-spin" /> Loading plan…
                                    </div>
                                )}
                                {previewError && (
                                    <div className="text-sm text-rose-800 bg-rose-100 border border-rose-200 rounded p-3">
                                        {previewError}
                                    </div>
                                )}
                                {preview && <ResetPreviewBlock preview={preview} />}
                            </div>

                            <div className="mt-3 space-y-3">
                                {/* Inner Gate 1: irreversible-acknowledgement checkbox. */}
                                <label
                                    className="flex items-start gap-2 text-sm text-rose-900 cursor-pointer select-none"
                                    data-testid="danger-irreversible-ack-label"
                                >
                                    <Checkbox
                                        checked={irreversibleAck}
                                        onCheckedChange={(v) => setIrreversibleAck(v === true)}
                                        className="mt-0.5"
                                        disabled={submitting}
                                        data-testid="danger-irreversible-ack-checkbox"
                                    />
                                    <span>
                                        I understand this is <strong>irreversible</strong> and that the
                                        rotated super-admin password is shown only once.
                                    </span>
                                </label>

                                {/* Inner Gate 2: typed phrase. */}
                                <div className="space-y-2">
                                    <Label htmlFor="reset-phrase-input">
                                        Type <code className="bg-slate-100 px-1 rounded text-rose-800 font-semibold">{RESET_PHRASE}</code> to confirm
                                    </Label>
                                    <Input
                                        id="reset-phrase-input"
                                        autoComplete="off"
                                        spellCheck={false}
                                        value={phrase}
                                        onChange={(e) => setPhrase(e.target.value)}
                                        placeholder={RESET_PHRASE}
                                        disabled={submitting}
                                        data-testid="danger-reset-phrase-input"
                                    />
                                </div>

                                {submitError && (
                                    <p className="text-sm text-rose-800" data-testid="danger-reset-error">
                                        {submitError}
                                    </p>
                                )}
                            </div>

                            <DialogFooter className="mt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={closeResetModal}
                                    disabled={submitting}
                                    data-testid="danger-reset-cancel-btn"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={onConfirmReset}
                                    disabled={!canSubmit}
                                    data-testid="danger-reset-submit-btn"
                                >
                                    {submitting ? (
                                        <><Loader2 size={14} className="mr-2 animate-spin" /> Resetting…</>
                                    ) : innerGatesSatisfied && countdown > 0 ? (
                                        <>Wait {countdown}s…</>
                                    ) : (
                                        <>Confirm reset</>
                                    )}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Card>
    );
}

function ResetPreviewBlock({ preview }) {
    const wipeRows = preview.wipe_plan.filter((r) => r.current > 0);
    const preserveRows = preview.preserve_plan;
    return (
        <div className="space-y-3 text-sm" data-testid="danger-preview-block">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Super admin (kept)</p>
                    <p className="font-mono text-slate-900 mt-1">{preview.super_admin?.email || "—"}</p>
                </div>
                <div className="rounded border border-slate-200 bg-white p-3">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Tenant logo</p>
                    <p className="text-slate-900 mt-1">
                        {preview.logo?.path && preview.logo?.exists ? "Will be preserved"
                            : "None configured (every uploaded file will be deleted)"}
                    </p>
                </div>
            </div>

            <div className="rounded border border-rose-200 bg-rose-50 p-3">
                <p className="text-xs uppercase tracking-wider text-rose-800 font-semibold">
                    Will delete
                </p>
                <table className="mt-2 w-full text-sm">
                    <tbody>
                        <tr className="border-b border-rose-100">
                            <td className="py-1 pr-2 font-mono text-slate-700">users (except super)</td>
                            <td className="py-1 text-right font-semibold text-rose-800">
                                {preview.users_plan.will_delete}
                            </td>
                        </tr>
                        {wipeRows.map((r) => (
                            <tr key={r.name} className="border-b border-rose-100">
                                <td className="py-1 pr-2 font-mono text-slate-700">{r.name}</td>
                                <td className="py-1 text-right font-semibold text-rose-800">{r.will_delete}</td>
                            </tr>
                        ))}
                        <tr>
                            <td className="py-1 pr-2 font-mono text-slate-700">files on disk</td>
                            <td className="py-1 text-right font-semibold text-rose-800">{preview.files_to_delete}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-xs uppercase tracking-wider text-emerald-900 font-semibold">
                    Will preserve
                </p>
                <table className="mt-2 w-full text-sm">
                    <tbody>
                        {preserveRows.map((r) => (
                            <tr key={r.name}>
                                <td className="py-1 pr-2 font-mono text-slate-700">{r.name}</td>
                                <td className="py-1 text-right font-semibold text-emerald-800">{r.will_keep}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {preview.unknown_collections?.length > 0 && (
                <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <p className="font-semibold">
                        Warning — {preview.unknown_collections.length} collection(s) the script does not recognize:
                    </p>
                    <ul className="list-disc ml-5 mt-1">
                        {preview.unknown_collections.map((c) => (
                            <li key={c.name}>
                                <code>{c.name}</code> (count={c.current}) — will NOT be wiped or preserved.
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function ResetCompleteView({ result, copied, onCopy, onLogout }) {
    return (
        <div data-testid="danger-reset-complete">
            <DialogHeader>
                <DialogTitle className="text-emerald-800">Reset complete</DialogTitle>
                <DialogDescription>
                    The database has been wiped to a fresh state. Save the new password below — it is shown <strong>only once</strong>.
                </DialogDescription>
            </DialogHeader>

            <div className="mt-4 rounded-md border-2 border-amber-300 bg-amber-50 p-4">
                <p className="text-xs uppercase tracking-wider text-amber-900 font-semibold">
                    New super admin password
                </p>
                <div className="mt-2 flex items-center gap-2">
                    <code
                        className="flex-1 font-mono text-base bg-white border border-amber-200 rounded px-3 py-2 select-all"
                        data-testid="danger-new-password"
                    >
                        {result.new_password}
                    </code>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={onCopy}
                        data-testid="danger-copy-password-btn"
                    >
                        <Copy size={14} className="mr-1" /> {copied ? "Copied!" : "Copy"}
                    </Button>
                </div>
                <p className="text-xs text-amber-900 mt-2">
                    Login email: <strong>{result.super_admin_email}</strong>
                </p>
            </div>

            <div className="mt-4 text-sm space-y-1">
                <p className="text-slate-700">
                    Wiped: {Object.values(result.deleted_counts || {}).reduce((a, b) => a + (b || 0), 0)} rows
                    across {Object.keys(result.deleted_counts || {}).length} collections.
                </p>
                <p className="text-slate-700">
                    Files removed: <strong>{result.files_deleted}</strong> · kept (logo etc.): <strong>{result.files_kept}</strong>
                </p>
                {result.failures?.length > 0 && (
                    <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-2 text-rose-900 text-xs">
                        <strong>{result.failures.length} failure(s):</strong>
                        <ul className="list-disc ml-5">
                            {result.failures.map((f, i) => (
                                <li key={i}><code>{f.target}</code>: {f.error}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <DialogFooter className="mt-6">
                <Button
                    type="button"
                    onClick={onLogout}
                    data-testid="danger-logout-after-reset-btn"
                >
                    Log out & sign back in
                </Button>
            </DialogFooter>
        </div>
    );
}

export default DangerZoneCard;
